import React, { useState, useEffect } from 'react';
import Marquee from 'react-fast-marquee';
import { AnimatePresence } from 'framer-motion';

import Layout from '../components/layout/layout';
import Container from '../components/common/container';
import Hero from '../components/home/sections/hero';
import Stats from '../components/home/sections/stats';
import UseCases from '../components/home/sections/use_cases';
import AgentsSection from '../components/home/sections/agents';
import Features from '../components/home/sections/features';
import Conversion from '../components/home/sections/conversion';
import Image from '../components/common/image';
import { getPartnerLogos } from '../config/images';

export default function Home({ initialData }) {
  const [showDeadstock, setShowDeadstock] = useState(false);
  const [contentIndex, setContentIndex] = useState(0); // Track content switching
  const partnerLogos = getPartnerLogos(initialData);

  useEffect(() => {
    // Get title from initialData if available, otherwise use fallback
    const pageTitle = initialData?.meta?.title || 'WymBee - Conversational Commerce Platform';
    document.title = pageTitle;

    // Handle custom scripts
    if (initialData?.meta?.custom_scripts) {
      Object.entries(initialData.meta.custom_scripts).forEach(([scriptId, scriptContent]) => {
        // Check if script already exists
        let existingScript = document.getElementById(scriptId);
        if (!existingScript) {
          const script = document.createElement('script');
          script.id = scriptId;
          script.type = 'text/javascript';
          script.innerHTML = scriptContent;
          document.head.appendChild(script);
        }
      });
    }

    // Cleanup function to remove scripts when component unmounts
    return () => {
      if (initialData?.meta?.custom_scripts) {
        Object.keys(initialData.meta.custom_scripts).forEach((scriptId) => {
          const script = document.getElementById(scriptId);
          if (script) {
            script.remove();
          }
        });
      }
    };
  }, [initialData]);

  return (
    <Layout initialData={initialData}>
      {/* Hero Section */}
      <Hero 
        showDeadstock={showDeadstock} 
        setShowDeadstock={setShowDeadstock}
        contentIndex={contentIndex}
        setContentIndex={setContentIndex}
        initialData={initialData} 
      />
      
      {/* Partner Logos */}
      <div className="bg-primary py-6 md:py-10 mt-8 md:mt-16">
        <Container>
          <div className="flex items-center">
            <div className="hidden md:flex items-center pr-8 border-r border-white/20">
              <span 
                className="text-white/90 text-xs md:text-sm font-medium uppercase tracking-wide whitespace-nowrap animate-fade-in-once"
              >
                Supported By
              </span>
            </div>
            <div className="flex-grow overflow-hidden">
              <Marquee
                gradient={false}
                speed={40}
                pauseOnHover={true}
                className="opacity-70"
              >
                {partnerLogos.map((logo, index) => (
                  <Image
                    key={index}
                    src={logo}
                    alt={`Partner ${index + 1}`}
                    className="h-6 md:h-8 grayscale mx-8"
                    initialData={initialData}
                    onError={(e) => {
                      console.error(`Failed to load image: ${logo}`);
                    }}
                  />
                ))}
              </Marquee>
            </div>
          </div>
        </Container>
      </div>
      
      {/* Stats Section - now receives contentIndex */}
      <Stats 
        showDeadstock={showDeadstock} 
        contentIndex={contentIndex}
        initialData={initialData} 
      />
      
      <AnimatePresence mode="wait">
        {!showDeadstock && (
          <>
            <UseCases key="use-cases-section" />
          </>
        )}
      </AnimatePresence>
      
      <Features 
        showDeadstock={showDeadstock} 
        contentIndex={contentIndex}
        initialData={initialData} 
      />
      
      <AnimatePresence mode="wait">
        {!showDeadstock && (
          <>
            <AgentsSection key="agents-section" initialData={initialData} />
          </>
        )}
      </AnimatePresence>
      
      <Conversion 
        showDeadstock={showDeadstock} 
        contentIndex={contentIndex}
        initialData={initialData} 
      />
    </Layout>
  );
}